import { useState } from 'react';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { toast } from 'sonner';
import type { components } from '@worldathletics/api-client';

import { Button, Pagination } from '@/components/atoms';
import {
  PageContentLayout,
  PageContentLayoutHeader,
  PageContentLayoutMain,
} from '@/components/layout';
import { ModalComponent } from '@/components/modal';
import { MODAL_CONFIRM_STYLES } from '@/components/modal/constants';
import { ConfirmModal, EditUserModal } from '@/components/modals';
import { CustomTag, CustomTitle } from '@/components/typography';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';
import { editorStore } from '@/context/store/store';
import { formatShortDate } from '@/lib/format-date';
import { PermissionGate } from '@/context/auth/permission-gate';
import { hasPermission, Permission } from '@worldathletics/utilities';
import {
  useDeleteUserMutation,
  useUpdateUserMutation,
  useUsersQuery,
} from '@/query/users';

type User = components['schemas']['UserResponseDto'];

const usersSearchSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(10),
});

type UsersSearch = z.infer<typeof usersSearchSchema>;

export const Route = createFileRoute('/_auth/users')({
  validateSearch: usersSearchSchema,
  beforeLoad: () => {
    const role = editorStore.getState().auth.user?.role;
    if (!role || !hasPermission(role, Permission.UsersRead)) {
      throw redirect({ to: '/documents' });
    }
  },
  component: UsersPage,
});

function UsersPage() {
  const params = Route.useSearch();
  const navigate = useNavigate();
  const [editing, setEditing] = useState<User | null>(null);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);

  const { data, isLoading } = useUsersQuery({
    page: params.page,
    limit: params.limit,
  });

  const updateMutation = useUpdateUserMutation();
  const deleteMutation = useDeleteUserMutation();

  const goToPage = (nextPage: number) => {
    void navigate({
      to: '/users',
      search: prev => ({ ...(prev as UsersSearch), page: nextPage }),
    });
  };

  const handleEnable = (user: User) => {
    updateMutation.mutate({ id: user.id, body: { isActive: true } });
  };

  const handleConfirmDelete = () => {
    if (deleteMutation.isPending || !userToDelete) return;
    deleteMutation.mutate(userToDelete.id, {
      onSuccess: () => {
        toast.success('User deleted');
        setUserToDelete(null);
      },
    });
  };

  return (
    <PageContentLayout>
      <PageContentLayoutHeader>
        <CustomTitle level="h1">Users</CustomTitle>
      </PageContentLayoutHeader>

      <PageContentLayoutMain>
        {isLoading ? (
          <p>Loading…</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-1/2 font-bold text-xl">Email</TableHead>
                <TableHead className="w-1/8 font-bold text-xl">Role</TableHead>
                <TableHead className="w-1/8 font-bold text-xl">Status</TableHead>
                <TableHead className="w-1/8 font-bold text-xl">Last login</TableHead>
                <TableHead className="w-1/8 font-bold text-xl">Created</TableHead>
                <TableHead className="w-1/4 font-bold text-xl">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.data.map(user => (
                <TableRow key={user.id}>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.role}</TableCell>
                  <TableCell>
                    <CustomTag
                      data-state={user.isActive ? 'active' : 'disabled'}
                    >
                      {user.isActive ? 'Active' : 'Disabled'}
                    </CustomTag>
                  </TableCell>
                  <TableCell>{formatShortDate(user.lastLoginAt)}</TableCell>
                  <TableCell>{formatShortDate(user.createdAt)}</TableCell>
                  <TableCell className="flex items-center gap-3">
                    <PermissionGate permission={Permission.UsersManage}>
                      <Button
                        type="button"
                        variant="info"
                        onClick={() => setEditing(user)}
                      >
                        Edit
                      </Button>{' '}
                      {user.isActive ? (
                        <Button
                          type="button"
                          variant="alert"
                          onClick={() => setUserToDelete(user)}
                          disabled={deleteMutation.isPending}
                        >
                          Delete
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          variant="default"
                          onClick={() => handleEnable(user)}
                          disabled={updateMutation.isPending}
                        >
                          Enable
                        </Button>
                      )}
                    </PermissionGate>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {data?.meta && data.meta.totalPages > 0 && (
          <div className="py-8">
            <Pagination
              className=""
              currentPage={data.meta.page}
              totalPages={data.meta.totalPages}
              onPreviousPageCall={() => goToPage(data.meta.page - 1)}
              onNextPageCall={() => goToPage(data.meta.page + 1)}
            />
          </div>
        )}
      </PageContentLayoutMain>


      <ModalComponent
        title="Edit user"
        isOpen={editing !== null}
        setIsOpen={open => {
          if (!open) setEditing(null);
        }}
        body={
          editing ? (
            <EditUserModal
              user={editing}
              isOpen={editing !== null}
              setIsOpen={open => {
                if (!open) setEditing(null);
              }}
            />
          ) : null
        }
      />

      <ModalComponent
        title="Delete user?"
        isOpen={userToDelete !== null}
        setIsOpen={open => {
          if (!open && !deleteMutation.isPending) setUserToDelete(null);
        }}
        style={MODAL_CONFIRM_STYLES}
        body={
          userToDelete ? (
            <ConfirmModal
              destructive
              message={`Delete ${userToDelete.email}? This will permanently remove their account. This cannot be undone.`}
              confirmLabel="Delete"
              isPending={deleteMutation.isPending}
              onConfirm={handleConfirmDelete}
              onCancel={() => setUserToDelete(null)}
            />
          ) : null
        }
      />
    </PageContentLayout>
  );
}
